/**
 * VirtualTable — tabla con virtualización/windowing (R16.2).
 *
 * Cuando el número de filas alcanza o supera un umbral configurable
 * (mínimo y valor por defecto: 100 filas), la tabla materializa en el DOM
 * **solo las filas visibles** dentro del viewport de scroll, más un
 * pequeño margen (`overscan`). El resto del alto se simula con espaciadores
 * superior/inferior, de modo que la barra de scroll y la posición relativa
 * de cada fila se mantienen exactas sin renderizar miles de `<tr>`.
 *
 * Por debajo del umbral se renderiza la tabla completa (sin overhead de
 * windowing), preservando el comportamiento nativo para listas pequeñas.
 *
 * Implementación propia y ligera (scroll + slice por viewport), sin añadir
 * dependencias nuevas. Reutiliza las clases `data-table`/`table-wrapper`
 * del tema para mantener la consistencia visual.
 */
import {
  useCallback,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";

/** Umbral mínimo (y por defecto) de filas para activar la virtualización (R16.2). */
export const VIRTUALIZATION_MIN_THRESHOLD = 100;

/** Definición de una columna de la VirtualTable. */
export interface VirtualColumn<T> {
  /** Clave estable de la columna. */
  key: string;
  /** Encabezado de la columna. */
  header: ReactNode;
  /** Render de la celda para una fila. */
  cell: (row: T, index: number) => ReactNode;
  /** Clase CSS aplicada a la celda y al encabezado (ej. "numeric"). */
  className?: string;
}

export interface VirtualTableProps<T> {
  /** Filas a renderizar. */
  rows: T[];
  /** Columnas de la tabla. */
  columns: VirtualColumn<T>[];
  /** Clave estable por fila (para `key` de React). */
  rowKey: (row: T, index: number) => string;
  /**
   * Umbral de filas a partir del cual se activa la virtualización.
   * Se acota a un mínimo de {@link VIRTUALIZATION_MIN_THRESHOLD} (R16.2).
   * Por defecto, 100.
   */
  threshold?: number;
  /** Alto fijo de cada fila en píxeles (necesario para el windowing). */
  rowHeight?: number;
  /** Alto del viewport scrollable en píxeles. */
  height?: number;
  /** Filas extra renderizadas fuera del viewport para suavizar el scroll. */
  overscan?: number;
  /** Clase adicional para el `<table>`. */
  className?: string;
}

/**
 * Calcula el rango de filas visibles `[start, end)` dado el desplazamiento
 * de scroll, las dimensiones del viewport y el overscan. Función pura para
 * facilitar el razonamiento y el testeo.
 */
export function computeVisibleRange(
  scrollTop: number,
  viewportHeight: number,
  rowHeight: number,
  rowCount: number,
  overscan: number,
): { start: number; end: number } {
  if (rowCount === 0 || rowHeight <= 0) return { start: 0, end: 0 };
  const first = Math.floor(scrollTop / rowHeight);
  const visibleCount = Math.ceil(viewportHeight / rowHeight);
  const start = Math.max(0, first - overscan);
  const end = Math.min(rowCount, first + visibleCount + overscan);
  return { start, end };
}

export function VirtualTable<T>({
  rows,
  columns,
  rowKey,
  threshold = VIRTUALIZATION_MIN_THRESHOLD,
  rowHeight = 36,
  height = 480,
  overscan = 8,
  className,
}: VirtualTableProps<T>): JSX.Element {
  const [scrollTop, setScrollTop] = useState(0);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // El umbral nunca baja del mínimo exigido por R16.2 (100 filas).
  const effectiveThreshold = Math.max(VIRTUALIZATION_MIN_THRESHOLD, threshold);
  const virtualize = rows.length >= effectiveThreshold;

  const onScroll = useCallback(() => {
    if (scrollRef.current) setScrollTop(scrollRef.current.scrollTop);
  }, []);

  const { start, end } = useMemo(
    () =>
      virtualize
        ? computeVisibleRange(scrollTop, height, rowHeight, rows.length, overscan)
        : { start: 0, end: rows.length },
    [virtualize, scrollTop, height, rowHeight, rows.length, overscan],
  );

  const tableEl = (
    <table className={`data-table${className ? ` ${className}` : ""}`}>
      <thead>
        <tr>
          {columns.map((c) => (
            <th key={c.key} className={c.className}>
              {c.header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {virtualize && start > 0 && (
          <tr aria-hidden="true" style={{ height: start * rowHeight }}>
            <td colSpan={columns.length} style={{ padding: 0, border: 0 }} />
          </tr>
        )}
        {rows.slice(start, end).map((row, i) => {
          const index = start + i;
          return (
            <tr key={rowKey(row, index)} style={virtualize ? { height: rowHeight } : undefined}>
              {columns.map((c) => (
                <td key={c.key} className={c.className}>
                  {c.cell(row, index)}
                </td>
              ))}
            </tr>
          );
        })}
        {virtualize && end < rows.length && (
          <tr aria-hidden="true" style={{ height: (rows.length - end) * rowHeight }}>
            <td colSpan={columns.length} style={{ padding: 0, border: 0 }} />
          </tr>
        )}
      </tbody>
    </table>
  );

  if (!virtualize) {
    return <div className="table-wrapper">{tableEl}</div>;
  }

  const scrollStyle: CSSProperties = { maxHeight: height, overflowY: "auto" };
  return (
    <div className="table-wrapper" data-virtualized="true">
      <div
        ref={scrollRef}
        onScroll={onScroll}
        style={scrollStyle}
        role="region"
        aria-label="Tabla virtualizada con scroll"
      >
        {tableEl}
      </div>
    </div>
  );
}
